import { useState, useEffect, useCallback } from 'react';
import { demoQuestions } from '../../data/demoQuestions';
import { DemoThemeProvider } from './DemoThemeContext';
import LaunchSequence from './LaunchSequence';
import FullscreenPrompt from './FullscreenPrompt';
import ExamLayout from './ExamLayout';
import AIProcessing from './AIProcessing';
import ResultsDashboard from './ResultsDashboard';

const TOTAL_TIME = 45 * 60; // 45 minutes in seconds

const initialStatus = () => {
  const status = {};
  demoQuestions.forEach((q, i) => {
    status[q.id] = i === 0 ? 'current' : 'unvisited';
  });
  return status;
};

export default function DemoPage() {
  const [phase, setPhase] = useState('launch'); // launch | fullscreen | exam | processing | results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionStatus, setQuestionStatus] = useState(initialStatus);
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAutoSave, setShowAutoSave] = useState(false);
  const [timeUsed, setTimeUsed] = useState(TOTAL_TIME); // time remaining at submit

  // ── beforeunload warning ──────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'exam') return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'Your assessment is currently in progress. Are you sure you want to leave?';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  // ── Fullscreen state listener ─────────────────────────────────────
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ── Timer countdown ───────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'exam') return;
    if (timeLeft <= 0) {
      setTimeUsed(0);
      setPhase('processing');
      return;
    }
    const tick = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(tick);
          setTimeUsed(0);
          setPhase('processing');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, timeLeft]);

  // ── Auto-save indicator every 15s ────────────────────────────────
  useEffect(() => {
    if (phase !== 'exam') return;
    const interval = setInterval(() => {
      setShowAutoSave(true);
      setTimeout(() => setShowAutoSave(false), 1500);
    }, 15000);
    return () => clearInterval(interval);
  }, [phase]);

  // ── Keyboard shortcuts ────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'exam') return;
    const handler = (e) => {
      if (!e.altKey) return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setCurrentQuestion((q) => Math.min(q + 1, demoQuestions.length - 1));
      } else if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setCurrentQuestion((q) => Math.max(q - 1, 0));
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        setQuestionStatus((prev) => {
          const qId = demoQuestions[currentQuestion]?.id;
          if (!qId) return prev;
          return {
            ...prev,
            [qId]: prev[qId] === 'review'
              ? prev[qId] === 'answered' ? 'answered' : 'skipped'
              : 'review',
          };
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentQuestion]);

  // ── Helpers ───────────────────────────────────────────────────────
  const navigateToQuestion = useCallback((newIndex) => {
    const oldQ = demoQuestions[currentQuestion];
    const newQ = demoQuestions[newIndex];
    if (!oldQ || !newQ || newIndex === currentQuestion) return;

    setQuestionStatus((prev) => {
      const updated = { ...prev };
      // Mark old question
      if (updated[oldQ.id] === 'current') {
        updated[oldQ.id] = answers[oldQ.id] !== undefined ? 'answered' : 'skipped';
      }
      // Mark new as current
      if (updated[newQ.id] !== 'review' && updated[newQ.id] !== 'answered') {
        updated[newQ.id] = 'current';
      } else {
        updated[newQ.id] = updated[newQ.id]; // keep answered/review
      }
      return updated;
    });

    setCurrentQuestion(newIndex);
  }, [currentQuestion, answers]);

  const setAnswer = useCallback((qId, value) => {
    setAnswers((prev) => ({ ...prev, [qId]: value }));
    setQuestionStatus((prev) => ({
      ...prev,
      [qId]: prev[qId] === 'review' ? 'review' : 'answered',
    }));
  }, []);

  const toggleReview = useCallback((qId) => {
    setQuestionStatus((prev) => ({
      ...prev,
      [qId]: prev[qId] === 'review'
        ? answers[qId] !== undefined ? 'answered' : 'skipped'
        : 'review',
    }));
  }, [answers]);

  const handleSubmit = useCallback(() => {
    setTimeUsed(timeLeft);
    setPhase('processing');
  }, [timeLeft]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const enterFullscreen = () => {
    document.documentElement.requestFullscreen?.().catch(() => {});
    setPhase('exam');
  };

  const returnToLanding = () => {
    window.location.href = '/';
  };

  // ── Phase transitions ─────────────────────────────────────────────
  const handleLaunchComplete = () => setPhase('fullscreen');
  const handleProcessingComplete = () => setPhase('results');

  const renderPhase = () => {
    if (phase === 'launch') return <LaunchSequence onComplete={handleLaunchComplete} />;
    if (phase === 'fullscreen') return <FullscreenPrompt onEnter={enterFullscreen} onDecline={() => setPhase('exam')} />;
    if (phase === 'exam') return (
      <ExamLayout
        currentQuestion={currentQuestion}
        answers={answers}
        questionStatus={questionStatus}
        timeLeft={timeLeft}
        isFullscreen={isFullscreen}
        showAutoSave={showAutoSave}
        onNavigate={navigateToQuestion}
        onAnswer={setAnswer}
        onSubmit={handleSubmit}
        onToggleFullscreen={toggleFullscreen}
        onToggleReview={toggleReview}
      />
    );
    if (phase === 'processing') return <AIProcessing onComplete={handleProcessingComplete} />;
    if (phase === 'results') return <ResultsDashboard answers={answers} timeUsed={timeUsed} onReturnToLanding={returnToLanding} />;
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <DemoThemeProvider>
      {renderPhase()}
    </DemoThemeProvider>
  );
}